import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

function authHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

export interface CalendarSettings {
  businessId: string;
  businessSlug: string;
  timezone: string;
  slotDurationMinutes: number;
  bufferMinutes: number;
  minNoticeHours: number;
  allowSameDayBooking: boolean;
  minNoticeMinutes: number;
  maxAdvanceDays: number;
  publicBookingEnabled: boolean;
  publicBookingIntro: string | null;
  publicBookingLogoUrl: string | null;
  publicNotificationEmails: string[];
}

export interface BookingType {
  type_id: string;
  name: string;
  color: string;
  icon: string | null;
  default_duration_minutes: number;
  is_public_bookable: 0 | 1 | boolean;
  is_active: 0 | 1 | boolean;
  display_order: number;
}

export interface AvailabilityRule {
  rule_id?: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
}

export interface BlockedDate {
  block_id: string;
  start_at: string;
  end_at: string;
  reason: string | null;
  created_at: string;
}

export interface BookingAssignee {
  user_id: string;
  role: 'owner' | 'attendee';
  response: 'pending' | 'accepted' | 'declined';
  responded_at: string | null;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface BookingRecipient {
  recipient_id?: string;
  user_id: string | null;
  external_email: string | null;
  notify_create: 0 | 1 | boolean;
  notify_update: 0 | 1 | boolean;
  notify_cancel: 0 | 1 | boolean;
  notify_reminder: 0 | 1 | boolean;
  reminder_minutes_before: number | null;
  first_name?: string;
  last_name?: string;
  user_email?: string;
}

export interface Booking {
  bookingId: string;
  businessId: string;
  typeId: string | null;
  typeName: string | null;
  typeColor: string | null;
  title: string;
  description: string | null;
  startAt: string;
  endAt: string;
  allDay: boolean;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';
  source: 'internal' | 'public';
  locationText: string | null;
  locationId: string | null;
  assetId: string | null;
  customer: {
    name: string | null;
    email: string | null;
    phone: string | null;
    company: string | null;
  };
  createdByUserId: string | null;
  cancelledByUserId: string | null;
  cancellationReason: string | null;
  externalToken: string | null;
  linkedSafetyCheckId: string | null;
  linkedLoanId: string | null;
  linkedOutgoingDeliveryRequestId: string | null;
  createdAt: string;
  updatedAt: string;
  assignees?: BookingAssignee[];
  recipients?: BookingRecipient[];
}

export interface AggregatedEvent {
  kind: 'booking' | 'safety_check' | 'loan_return' | 'outgoing_delivery' | 'shift';
  id: string;
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  color: string;
  status?: string;
  typeName?: string;
  typeId?: string;
}

export const calendarApi = {
  // Settings
  async getSettings() {
    const r = await axios.get<{ success: boolean; settings: CalendarSettings }>(
      `${API_BASE_URL}/calendar/settings`,
      { headers: authHeaders() }
    );
    return r.data.settings;
  },
  async updateSettings(patch: Partial<CalendarSettings>) {
    const r = await axios.put(`${API_BASE_URL}/calendar/settings`, patch, { headers: authHeaders() });
    return r.data;
  },
  async checkSlugAvailable(slug: string) {
    const r = await axios.get(`${API_BASE_URL}/calendar/slug-available`, {
      params: { slug },
      headers: authHeaders(),
    });
    return r.data as { available: boolean; reason?: string; slug?: string };
  },

  // Availability
  async getAvailabilityRules() {
    const r = await axios.get<{ success: boolean; rules: AvailabilityRule[] }>(
      `${API_BASE_URL}/calendar/availability-rules`,
      { headers: authHeaders() }
    );
    return r.data.rules;
  },
  async replaceAvailabilityRules(rules: AvailabilityRule[]) {
    const r = await axios.put(`${API_BASE_URL}/calendar/availability-rules`, { rules }, { headers: authHeaders() });
    return r.data;
  },

  // Blocked dates
  async getBlockedDates() {
    const r = await axios.get<{ success: boolean; blocks: BlockedDate[] }>(
      `${API_BASE_URL}/calendar/blocked-dates`,
      { headers: authHeaders() }
    );
    return r.data.blocks;
  },
  async addBlockedDate(payload: { start_at: string; end_at: string; reason?: string }) {
    const r = await axios.post(`${API_BASE_URL}/calendar/blocked-dates`, payload, { headers: authHeaders() });
    return r.data;
  },
  async deleteBlockedDate(id: string) {
    const r = await axios.delete(`${API_BASE_URL}/calendar/blocked-dates/${id}`, { headers: authHeaders() });
    return r.data;
  },

  // Booking types
  async getBookingTypes() {
    const r = await axios.get<{ success: boolean; types: BookingType[] }>(
      `${API_BASE_URL}/calendar/booking-types`,
      { headers: authHeaders() }
    );
    return r.data.types;
  },
  async createBookingType(payload: Partial<BookingType>) {
    const r = await axios.post(`${API_BASE_URL}/calendar/booking-types`, payload, { headers: authHeaders() });
    return r.data;
  },
  async updateBookingType(id: string, payload: Partial<BookingType>) {
    const r = await axios.put(`${API_BASE_URL}/calendar/booking-types/${id}`, payload, { headers: authHeaders() });
    return r.data;
  },
  async deleteBookingType(id: string) {
    const r = await axios.delete(`${API_BASE_URL}/calendar/booking-types/${id}`, { headers: authHeaders() });
    return r.data;
  },

  // Public notification users
  async getPublicNotificationUsers() {
    const r = await axios.get(`${API_BASE_URL}/calendar/public-notification-users`, { headers: authHeaders() });
    return r.data.users as Array<{ user_id: string; email: string; first_name: string; last_name: string }>;
  },
  async setPublicNotificationUsers(userIds: string[]) {
    const r = await axios.put(
      `${API_BASE_URL}/calendar/public-notification-users`,
      { userIds },
      { headers: authHeaders() }
    );
    return r.data;
  },

  // Bookings
  async listBookings(params: { from?: string; to?: string; typeId?: string; status?: string; assigneeId?: string; source?: string } = {}) {
    const r = await axios.get<{ success: boolean; bookings: Booking[] }>(
      `${API_BASE_URL}/bookings`,
      { params, headers: authHeaders() }
    );
    return r.data.bookings;
  },
  async aggregated(params: { from?: string; to?: string; includeOverlays?: boolean } = {}) {
    const r = await axios.get<{ success: boolean; events: AggregatedEvent[] }>(
      `${API_BASE_URL}/bookings/aggregated`,
      { params, headers: authHeaders() }
    );
    return r.data.events;
  },
  async getBooking(id: string) {
    const r = await axios.get<{ success: boolean; booking: Booking }>(
      `${API_BASE_URL}/bookings/${id}`,
      { headers: authHeaders() }
    );
    return r.data.booking;
  },
  async createBooking(payload: Partial<Booking> & { assignees?: any[]; recipients?: any[] }) {
    const r = await axios.post(`${API_BASE_URL}/bookings`, payload, { headers: authHeaders() });
    return r.data.booking as Booking;
  },
  async updateBooking(id: string, patch: any) {
    const r = await axios.put(`${API_BASE_URL}/bookings/${id}`, patch, { headers: authHeaders() });
    return r.data.booking as Booking;
  },
  async cancelBooking(id: string, reason?: string) {
    const r = await axios.post(`${API_BASE_URL}/bookings/${id}/cancel`, { reason }, { headers: authHeaders() });
    return r.data;
  },
  async confirmBooking(id: string) {
    const r = await axios.post(`${API_BASE_URL}/bookings/${id}/confirm`, {}, { headers: authHeaders() });
    return r.data;
  },
  async completeBooking(id: string, noShow = false) {
    const r = await axios.post(`${API_BASE_URL}/bookings/${id}/complete`, { noShow }, { headers: authHeaders() });
    return r.data;
  },
};

export default calendarApi;
